import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Cookie, Settings, Shield, CheckCircle2, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { openCookiePreferences, resetCookieConsent } from '../components/CookieConsentBanner';

const CookiePolicyPage = () => {
  const handleManageCookies = () => {
    openCookiePreferences();
  };

  const handleResetConsent = () => {
    resetCookieConsent();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Cookie className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Cookie Policy</h1>
            <p className="text-muted-foreground">Last updated: February 2026</p>
          </div>

          <div className="prose prose-lg max-w-none text-muted-foreground space-y-8">
            <section className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">What Are Cookies?</h2>
              <p>Cookies are small text files stored on your device when you visit websites. They help us provide you with a better experience by remembering your preferences and understanding how you use our platform.</p>
            </section>

            <section className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-400" />
                Your Consent
              </h2>
              <p className="mb-4">
                When you first visit FundTheHope, we display a cookie consent banner that allows you to choose which cookies to accept. 
                Essential cookies are always active as they are necessary for the website to function. Analytics and marketing cookies 
                require your explicit opt-in consent.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleManageCookies}
                  variant="outline"
                  className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Cookie Preferences
                </Button>
                <Button 
                  onClick={handleResetConsent}
                  variant="ghost"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset All Consent
                </Button>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">Types of Cookies We Use</h2>
              
              <div className="bg-card border border-border rounded-xl p-6 my-4">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-medium text-foreground">Essential Cookies</h3>
                  <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">Required</span>
                </div>
                <p className="text-sm mb-3">Required for the website to function. They enable core features like security, account access, and payment processing. These cookies cannot be disabled.</p>
                <div className="bg-secondary/30 rounded-lg p-3 text-sm">
                  <p className="font-medium text-foreground mb-2">Examples:</p>
                  <ul className="space-y-1 text-muted-foreground">
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Session authentication cookies</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Security tokens (CSRF protection)</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Load balancing cookies</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Cookie consent preferences</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-6 my-4">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-medium text-foreground">Analytics Cookies</h3>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">Requires Consent</span>
                </div>
                <p className="text-sm mb-3">Help us understand how visitors interact with our website, allowing us to improve user experience. These cookies are only set after you give consent.</p>
                <div className="bg-secondary/30 rounded-lg p-3 text-sm">
                  <p className="font-medium text-foreground mb-2">Examples:</p>
                  <ul className="space-y-1 text-muted-foreground">
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-blue-400" /> Google Analytics (_ga, _gid)</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-blue-400" /> Page view tracking</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-blue-400" /> User behavior analysis</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-blue-400" /> Performance monitoring</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-6 my-4">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-medium text-foreground">Marketing Cookies</h3>
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">Requires Consent</span>
                </div>
                <p className="text-sm mb-3">Used to deliver relevant advertisements and track ad campaign performance. These cookies are only set after you give consent.</p>
                <div className="bg-secondary/30 rounded-lg p-3 text-sm">
                  <p className="font-medium text-foreground mb-2">Examples:</p>
                  <ul className="space-y-1 text-muted-foreground">
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-purple-400" /> Ad targeting cookies</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-purple-400" /> Conversion tracking</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-purple-400" /> Retargeting cookies</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-purple-400" /> Social media pixels</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Cookie Retention</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left p-3 border-b border-border">Cookie Type</th>
                      <th className="text-left p-3 border-b border-border">Retention Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/50">
                      <td className="p-3">Session cookies</td>
                      <td className="p-3">Until browser is closed</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="p-3">Authentication cookies</td>
                      <td className="p-3">30 days (or until logout)</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="p-3">Consent preferences</td>
                      <td className="p-3">12 months</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="p-3">Google Analytics</td>
                      <td className="p-3">Up to 26 months</td>
                    </tr>
                    <tr>
                      <td className="p-3">Marketing cookies</td>
                      <td className="p-3">Varies by provider (typically 30 days - 2 years)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Managing Cookies</h2>
              <p className="mb-4">In addition to our cookie consent banner, you can control cookies through your browser settings:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                <li><strong>Edge:</strong> Settings → Privacy & Services → Cookies</li>
              </ul>
              <p className="mt-4 text-sm text-muted-foreground">Note: Disabling essential cookies may affect website functionality.</p>
            </section>

            <section className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Third-Party Cookies</h2>
              <p className="mb-3">Some cookies are placed by third-party services that appear on our pages:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Stripe:</strong> Payment processing and fraud prevention</li>
                <li><strong>Google Analytics:</strong> Website usage analytics (with consent)</li>
                <li><strong>Google Tag Manager:</strong> Tag management (with consent)</li>
              </ul>
              <p className="mt-4 text-sm">These third parties have their own privacy policies governing the use of their cookies.</p>
            </section>

            <section className="bg-gradient-to-r from-emerald-500/10 to-teal-600/10 border border-emerald-500/30 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Contact Us</h2>
              <p>If you have questions about our cookie policy or how we handle your data, please contact us:</p>
              <div className="mt-4 space-y-2">
                <p><strong className="text-foreground">Privacy Team:</strong> <a href="mailto:privacy@fundthehope.com" className="text-emerald-500 hover:underline">privacy@fundthehope.com</a></p>
                <p><strong className="text-foreground">General Support:</strong> <a href="mailto:support@fundthehope.com" className="text-emerald-500 hover:underline">support@fundthehope.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
